import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import businessOnboardingApi from '../../api/businessOnboarding';

export default function LocationLetterVerification() {
  const { businessId } = useParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState(null);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [addressActionRequired, setAddressActionRequired] = useState(false);

  const requestCurrentPosition = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported on this device.'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: Number(position.coords.latitude),
          longitude: Number(position.coords.longitude),
        });
      },
      (geoError) => {
        if (geoError?.code === geoError.PERMISSION_DENIED) {
          reject(new Error('Location permission is required. Please enable GPS and try again while at your business premises.'));
          return;
        }
        if (geoError?.code === geoError.POSITION_UNAVAILABLE) {
          reject(new Error('Unable to determine your location. Please ensure GPS is enabled and try again at your premises.'));
          return;
        }
        if (geoError?.code === geoError.TIMEOUT) {
          reject(new Error('Timed out getting your location. Please retry while at your premises.'));
          return;
        }
        reject(new Error('Failed to capture your current location.'));
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      }
    );
  });

  const loadStatus = useCallback(async () => {
    if (!businessId) return;

    setLoading(true);
    setError('');

    try {
      const response = await businessOnboardingApi.getLocationLetterStatus(businessId);
      setStatus(response?.data || null);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load location verification status');
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const mailStatusText = useMemo(() => {
    const mailStatus = status?.mailStatus;
    if (mailStatus === 'verified') return 'Verified';
    if (mailStatus === 'sent') return 'Letter sent';
    if (mailStatus === 'expired') return 'Code expired';
    return 'Not requested';
  }, [status]);

  const handleSubmitCode = async (event) => {
    event.preventDefault();
    if (!businessId) return;

    const normalizedCode = String(code || '').trim().toUpperCase();
    if (!/^[A-Z0-9]{6}$/.test(normalizedCode)) {
      setError('Please enter a valid 6-character code.');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');
    setAddressActionRequired(false);

    try {
      const gps = await requestCurrentPosition();
      await businessOnboardingApi.verifyLocationLetterCode(businessId, normalizedCode, gps);
      const capabilitiesResponse = await businessOnboardingApi.checkCapabilities(businessId);
      const caps = capabilitiesResponse?.data || {};
      const paymentsRequested = !!caps?.features?.reservations || !!caps?.features?.products;
      const needsStripeOnboarding = paymentsRequested && !caps?.canAcceptPayments;

      setSuccess(
        needsStripeOnboarding
          ? 'Address verified successfully. Redirecting to Stripe Connect...'
          : 'Address verified successfully. Redirecting...'
      );
      await loadStatus();
      setTimeout(() => {
        if (needsStripeOnboarding) {
          navigate(`/business/${businessId}/onboarding/stripe-refresh`, { replace: true });
          return;
        }
        navigate(`/business-admin?business_id=${businessId}`, { replace: true });
      }, 900);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to verify code');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!businessId) return;

    setResending(true);
    setError('');
    setSuccess('');
    setAddressActionRequired(false);

    try {
      await businessOnboardingApi.resendLocationLetter(businessId);
      setSuccess('A new verification letter has been sent.');
      await loadStatus();
    } catch (e) {
      const responseData = e?.response?.data || {};
      const provider = String(responseData?.provider || '').toLowerCase();
      const stage = String(responseData?.stage || '').toLowerCase();
      const details = Array.isArray(responseData?.details) ? responseData.details : [];
      const hasAddressResolveError =
        provider === 'pingen' &&
        (stage === 'resolve_place_address' ||
          details.some((item) =>
            ['conflict_state', 'invalid_char_count', 'address_not_resolved'].includes(
              String(item?.code || '').toLowerCase()
            )
          ));

      if (hasAddressResolveError) {
        setAddressActionRequired(true);
        setError('Postal provider could not validate this address. Please update your business location address and try again.');
      } else {
        setError(responseData?.message || e?.message || 'Failed to resend letter');
      }
    } finally {
      setResending(false);
    }
  };

  if (loading) {
    return (
      <div className="tailwind">
        <div className="flex min-h-[60dvh] w-full flex-col items-center justify-center gap-3 bg-gray-50 px-4">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-indigo-600" />
          <div className="text-sm text-gray-700">Loading location verification status…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="tailwind">
      <div className="mx-auto flex min-h-[70dvh] w-full max-w-xl flex-col justify-center gap-4 px-4 py-8">
        <h1 className="text-2xl font-bold text-gray-900">Verify business location</h1>
        <p className="text-sm text-gray-700">
          Business: <span className="font-semibold">{status?.businessName || 'Business'}</span>
        </p>

        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="text-sm text-gray-600">Mail status</div>
          <div className="mt-1 text-base font-semibold text-gray-900">{mailStatusText}</div>
          {status?.verificationCodeSentAt && (
            <div className="mt-2 text-xs text-gray-600">
              Sent: {new Date(status.verificationCodeSentAt).toLocaleString()}
            </div>
          )}
          {status?.verificationCodeExpiresAt && (
            <div className="text-xs text-gray-600">
              Expires: {new Date(status.verificationCodeExpiresAt).toLocaleString()}
            </div>
          )}
        </div>

        {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
        {addressActionRequired && (
          <button
            type="button"
            onClick={() => navigate(`/business/onboarding/${businessId}`)}
            className="rounded-md border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-700"
          >
            Update business address
          </button>
        )}
        {success && <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">{success}</div>}

        <form onSubmit={handleSubmitCode} className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="mb-3 text-xs text-amber-700">
            You must be physically at your business premises with GPS enabled to verify this code.
          </p>
          <label htmlFor="verificationCode" className="mb-2 block text-sm font-medium text-gray-700">
            Enter the 6-character code from your letter
          </label>
          <input
            id="verificationCode"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="ABC123"
            maxLength={6}
            autoComplete="off"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm uppercase tracking-wider focus:border-indigo-500 focus:outline-none"
          />
          <button
            type="submit"
            disabled={submitting}
            className="mt-3 w-full rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-70"
          >
            {submitting ? 'Verifying location…' : 'Verify code'}
          </button>
        </form>

        <button
          type="button"
          onClick={handleResend}
          disabled={resending}
          className="rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-700 disabled:cursor-not-allowed disabled:opacity-70"
        >
          {resending ? 'Sending…' : 'Did not receive it? Send another letter'}
        </button>
      </div>
    </div>
  );
}
